import { useContext, useEffect, useState } from 'react';
import AppContext from './../../contexts/AppContext';
import DataContext from './../../contexts/DataContext';
import ClickToAction from '../../components/ClickToAction';
import { Footer } from './components/Footer';
import { Form } from './components/Form';
import { PopupModal } from 'react-calendly';
import { DateTime } from 'luxon';

import logoDesktop from './../../images/isr_feature_form/logo_enphase.svg';
import logoMobile from './../../images/logo-icon.svg';
import iconAdvice from './../../images/isr_feature_form/icon_global_advice.svg';
import iconInstaller from './../../images/isr_feature_form/icon_global_installer.svg';
import iconCertifiedInstaller from './../../images/isr_feature_form/icon_global_certified_installers.svg';
import iconCheck from './../../images/isr_feature_form/green_check.svg';

export default function ShortISRFeatureForm() {
  const {
    showClickToCall,
    calendlyUrl,
    isShortISRFeatureFormCustomer,
    ISRAppointmentDetails,
    setStartedFunnel,
  } = useContext(AppContext);
  const { data } = useContext(DataContext);
  const [modalOpen, setModalOpen] = useState(false);

  const onOpenModal = () => setModalOpen(true);
  const onCloseModal = () => setModalOpen(false);

  useEffect(() => {
    if (isShortISRFeatureFormCustomer) onOpenModal();
  }, [isShortISRFeatureFormCustomer]);

  const reasons = [
    {
      icon: iconInstaller,
      text: 'Receive professional support as you consider a new solar system, battery backup, or system upgrade.',
    },
    {
      icon: iconCertifiedInstaller,
      text: 'Get matched with a certified Enphase installer in your area once we assess your unique needs.',
    },
    {
      icon: iconAdvice,
      text: 'Complete this quick form, and an Enphase solar specialist will contact you shortly.',
    },
  ];

  const [mobileReasonIndex, setMobileReasonIndex] = useState(0);

  /*
    Swipe for resons on mobile
    Ref: https://stackoverflow.com/a/70612770
  */
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // the required distance between touchStart and touchEnd to be detected as a swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e: any) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: any) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe || isRightSwipe)
      if (isLeftSwipe) {
        if (mobileReasonIndex === reasons.length - 1) {
          setMobileReasonIndex(0);
        } else {
          setMobileReasonIndex(mobileReasonIndex + 1);
        }
      } else {
        if (mobileReasonIndex === 0) {
          setMobileReasonIndex(reasons.length - 1);
        } else {
          setMobileReasonIndex(mobileReasonIndex - 1);
        }
      }
  };
  /*
    END: Swipe for resons on mobile
  */

  useEffect(() => {
    setStartedFunnel(true);
  }, []);

  return (
    <div
      className={`${
        isShortISRFeatureFormCustomer
          ? 'min-h-svh flex flex-col'
          : 'md:min-h-svh md:flex md:flex-col bg-[#dcdcd6]'
      } w-full justify-between text-black`}>
      <div className="h-[70px] bg-black flex justify-between">
        <img
          alt=""
          src={logoDesktop}
          className="hidden md:block w-[140px] ml-16"></img>
        <img alt="" src={logoMobile} className="md:hidden w-[24px] ml-6"></img>
        {showClickToCall && !isShortISRFeatureFormCustomer ? (
          <ClickToAction></ClickToAction>
        ) : null}
      </div>

      {isShortISRFeatureFormCustomer ? (
        <div className="p--6 md:p--16 text-center">
          {ISRAppointmentDetails ? (
            <div className="text-left mx-auto w-fit bg-white p-6 rounded-xl my-6">
              <div className="flex items-center">
                <p className="text-green-600 bg-green-200 px-4 py-2 rounded-md flex shrink-0">
                  <img src={iconCheck} />
                  <span>Confirmed!</span>
                </p>
                <p className="font-bold text-[18px] ml-3 leading-tight">
                  Your appointment is set with{' '}
                  {ISRAppointmentDetails.userName || (
                    <span className="loader-dots small !ml-[35px]"></span>
                  )}
                </p>
              </div>
              <hr className="my-5"></hr>
              <p className="my-1">
                <b>Meeting name: </b>
                Enphase solar consultation
              </p>
              <p className="my-1 flex">
                <b>Date & Time: </b>
                {ISRAppointmentDetails.startTime ? (
                  <span className="mx-1">
                    {DateTime.fromISO(
                      ISRAppointmentDetails.startTime
                    ).toLocaleString({
                      weekday: 'long',
                      month: 'long',
                      day: '2-digit',
                    })}
                    {' @ '}
                    {DateTime.fromISO(
                      ISRAppointmentDetails.startTime
                    ).toLocaleString({
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                ) : (
                  <span className="loader-dots small !ml-[35px]"></span>
                )}
              </p>
              <p className="my-1 flex">
                <b>Location: </b> <span className="mx-1">Phone meeting:</span>{' '}
                {ISRAppointmentDetails.phoneNumber ? (
                  <span>{ISRAppointmentDetails.phoneNumber}</span>
                ) : (
                  <span className="loader-dots small !ml-[35px]"></span>
                )}
              </p>
            </div>
          ) : (
            <>
              <div className='flex justify-center'>
                <a
                  href=""
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenModal();
                  }}
                  className="flex items-center text-black text-[16px] bg-enph-orange px-[14px] py-[7px] rounded-full hover:opacity-80 justify-center w-[90%] max-w-[250px]">
                  Book an appointment
                </a>
              </div>
              <PopupModal
                url={calendlyUrl}
                onModalClose={onCloseModal}
                open={modalOpen}
                rootElement={document.getElementById('root')!}
                prefill={{
                  email: data.email,
                  firstName: data.firstName,
                  lastName: data.lastName,
                  name: data.fullName,
                  customAnswers: {
                    a1: data.phone,
                    a2: data.selectedZipCode,
                  },
                }}
              />
            </>
          )}
        </div>
      ) : (
        <div className="p-6 md:p-16 max-w-[1600px] mx-auto">
          <div className="flex gap-16 flex-col md:flex-row">
            <div className="basis-1/2 text-[20px] leading-tight">
              <p className="text-[32px] md:text-[42px]">
                Expert guidance throughout your solar journey
              </p>
              <div className="mt-10 hidden md:block">
                {reasons.map((reason, index) => {
                  return (
                    <div className="flex gap-4 mt-8 items-center" key={index}>
                      <img alt="" src={reason.icon} className="w-10 h-10" />
                      <p>{reason.text}</p>
                    </div>
                  );
                })}
              </div>
              <div className="overflow-hidden">
                <div
                  className="mt-0 md:hidden flex w-[300%] transition-all"
                  style={{ marginLeft: `-${mobileReasonIndex * 100}%` }}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}>
                  {reasons.map((reason, index) => {
                    return (
                      <div
                        className="flex gap-4 mt-8 items-center w-full"
                        key={index}>
                        <img alt="" src={reason.icon} className="w-10 h-10" />
                        <p>{reason.text}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center mt-4 md:hidden">
                  <div className="flex gap-4">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        mobileReasonIndex === 0 ? 'bg-gray-500' : 'bg-white'
                      }`}
                      onClick={() => setMobileReasonIndex(0)}></div>
                    <div
                      className={`w-3 h-3 rounded-full ${
                        mobileReasonIndex === 1 ? 'bg-gray-500' : 'bg-white'
                      }`}
                      onClick={() => setMobileReasonIndex(1)}></div>
                    <div
                      className={`w-3 h-3 rounded-full ${
                        mobileReasonIndex === 2 ? 'bg-gray-500' : 'bg-white'
                      }`}
                      onClick={() => setMobileReasonIndex(2)}></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="basis-1/2">
              <Form></Form>
            </div>
          </div>
        </div>
      )}
      <Footer></Footer>
    </div>
  );
}
